import { useState, useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { crewOpsApi } from "../../api";
import {
  Printer, Save, RefreshCw, Plane, Users, Clock, ChevronDown, X,
  ChevronLeft, ChevronRight, TrendingUp, AlertTriangle, Plus, Wand2,
} from "lucide-react";
import { toast } from "sonner";

// ─── IST-aware today constants ────────────────────────────────────────────────
const IST_NOW       = new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000 - new Date().getTimezoneOffset() * 60000);
const TODAY_DAY     = IST_NOW.getDate();
const MONTH_NAME    = IST_NOW.toLocaleString("default", { month: "long" });
const DAYS_IN_MONTH = new Date(IST_NOW.getFullYear(), IST_NOW.getMonth() + 1, 0).getDate();

// ─── Indian Airport Database (B737 MAX 8 operable airports) ──────────────────
export const AIRPORTS = {
  // Tier-1 hubs
  DEL: { name: "Indira Gandhi International",        city: "New Delhi",      lat: 28.5665, lng: 77.1031 },
  BOM: { name: "Chhatrapati Shivaji International",  city: "Mumbai",         lat: 19.0896, lng: 72.8656 },
  BLR: { name: "Kempegowda International",           city: "Bengaluru",      lat: 13.1986, lng: 77.7066 },
  HYD: { name: "Rajiv Gandhi International",         city: "Hyderabad",      lat: 17.2403, lng: 78.4294 },
  MAA: { name: "Chennai International",              city: "Chennai",        lat: 12.9941, lng: 80.1709 },
  CCU: { name: "Netaji Subhas Chandra Bose Intl",    city: "Kolkata",        lat: 22.6533, lng: 88.4467 },
  // Tier-2
  GOA: { name: "Goa International (Manohar)",        city: "Goa",            lat: 15.3808, lng: 73.8314 },
  AMD: { name: "Sardar Vallabhbhai Patel Intl",      city: "Ahmedabad",      lat: 23.0773, lng: 72.6347 },
  JAI: { name: "Jaipur International",               city: "Jaipur",         lat: 26.8242, lng: 75.8122 },
  COK: { name: "Cochin International",               city: "Kochi",          lat: 10.1520, lng: 76.3998 },
  LKO: { name: "Chaudhary Charan Singh Intl",        city: "Lucknow",        lat: 26.7606, lng: 80.8893 },
  PNQ: { name: "Pune International",                 city: "Pune",           lat: 18.5822, lng: 73.9197 },
  NAG: { name: "Dr. Babasaheb Ambedkar Intl",        city: "Nagpur",         lat: 21.0922, lng: 79.0472 },
  IXC: { name: "Shaheed Bhagat Singh Intl",          city: "Chandigarh",     lat: 30.6735, lng: 76.7885 },
  ATQ: { name: "Sri Guru Ram Dass Jee Intl",         city: "Amritsar",       lat: 31.7096, lng: 74.7973 },
  GAU: { name: "Lokpriya Gopinath Bordoloi Intl",    city: "Guwahati",       lat: 26.1061, lng: 91.5859 },
  BBI: { name: "Biju Patnaik International",         city: "Bhubaneswar",    lat: 20.2444, lng: 85.8178 },
  VNS: { name: "Lal Bahadur Shastri International",  city: "Varanasi",       lat: 25.4522, lng: 82.8593 },
  // Additional B737 destinations
  LEH: { name: "Kushok Bakula Rimpochhe Airport",    city: "Leh",            lat: 34.1589, lng: 77.5465 },
  SXR: { name: "Sheikh ul-Alam International",       city: "Srinagar",       lat: 33.9871, lng: 74.7742 },
  IXJ: { name: "Jammu Airport",                      city: "Jammu",          lat: 32.6890, lng: 74.8374 },
  TRV: { name: "Trivandrum International",           city: "Thiruvananthapuram", lat: 8.4821, lng: 76.9201 },
  BHO: { name: "Raja Bhoj International",            city: "Bhopal",         lat: 23.2875, lng: 77.3374 },
  RPR: { name: "Swami Vivekananda Airport",          city: "Raipur",         lat: 21.1804, lng: 81.7388 },
  IXR: { name: "Birsa Munda Airport",                city: "Ranchi",         lat: 23.3143, lng: 85.3217 },
  UDR: { name: "Maharana Pratap Airport",            city: "Udaipur",        lat: 24.6177, lng: 73.8961 },
  IXB: { name: "Bagdogra International",             city: "Bagdogra",       lat: 26.6812, lng: 88.3286 },
  IXU: { name: "Aurangabad Airport",                 city: "Aurangabad",     lat: 19.8627, lng: 75.3981 },
  GAY: { name: "Gaya Airport",                       city: "Gaya",           lat: 24.7443, lng: 84.9512 },
  IXZ: { name: "Veer Savarkar International",        city: "Port Blair",     lat: 11.6412, lng: 92.7298 },
};

// ─── Block-hours helper: uses sector_detail.block_hours; falls back to FDP-based estimate ─
function getPairingBlockHours(pairing) {
  const sectorBH = (pairing.sector_detail || []).reduce((s, sd) => s + (parseFloat(sd.block_hours) || 0), 0);
  if (sectorBH > 0) return Math.round(sectorBH * 10) / 10;
  // Fallback when sector_detail is absent: FDP minus report (1h) + turnarounds (0.5h each) + debrief (0.5h)
  const fdp = parseFloat(pairing.total_fdp_hours) || 0;
  const n   = (pairing.sectors || []).length || 1;
  const overhead = 1.0 + Math.max(0, n - 1) * 0.5 + 0.5;
  return Math.max(0, Math.round((fdp - overhead) * 10) / 10);
}

// ─── Haversine block-hour estimator for Scenario Builder ─────────────────────
// B737 MAX 8 avg block speed ~650 km/h (includes climb/descent/taxi overhead)
function estimateBlockHours(depCode, arrCode) {
  const d = AIRPORTS[depCode], a = AIRPORTS[arrCode];
  if (!d || !a) return 1.5;
  const R = 6371;
  const lat1 = d.lat * Math.PI / 180, lat2 = a.lat * Math.PI / 180;
  const dLat = (a.lat - d.lat) * Math.PI / 180;
  const dLng = (a.lng - d.lng) * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const dist = 2 * R * Math.asin(Math.sqrt(h));
  return Math.max(0.5, Math.round((dist / 650 + 0.4) * 10) / 10);
}

// ─── Time arithmetic helper ───────────────────────────────────────────────────
function addMinutes(timeStr, minutes) {
  const [h, m] = (timeStr || "00:00").split(":").map(Number);
  const total  = h * 60 + m + Math.round(minutes);
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

// ─── Short aircraft-type label ────────────────────────────────────────────────
function acTypeShort(fullType = "") {
  if (fullType.includes("737 MAX 8")) return "B737 MAX 8";
  if (fullType.includes("320neo"))    return "A320neo";
  if (fullType.includes("320"))       return "A320";
  if (fullType.includes("321"))       return "A321";
  if (fullType.includes("777"))       return "B777";
  if (fullType.includes("ATR"))       return "ATR 72";
  return fullType;
}



// ─── Curved arc path ──────────────────────────────────────────────────────────
function arcPath(lat1, lng1, lat2, lng2, steps = 40) {
  const pts = [];
  const dLat = lat2 - lat1, dLng = lng2 - lng1;
  const len  = Math.sqrt(dLat * dLat + dLng * dLng) || 1;
  const curve = len * 0.18;
  for (let i = 0; i <= steps; i++) {
    const t   = i / steps;
    const lat = lat1 + dLat * t;
    const lng = lng1 + dLng * t;
    pts.push([
      lat - (dLng / len) * curve * Math.sin(Math.PI * t),
      lng + (dLat / len) * curve * Math.sin(Math.PI * t),
    ]);
  }
  return pts;
}

function parseSectors(sectors) {
  if (!sectors || !sectors.length) return [];
  const codes = [];
  sectors.forEach(s => {
    const parts = s.split("-");
    if (parts.length === 2) {
      if (!codes.length) codes.push(parts[0]);
      codes.push(parts[1]);
    }
  });
  return codes;
}

// ─── Deduplicated arc builder ─────────────────────────────────────────────────
function buildArcs(pairingList) {
  const arcMap = new Map();
  pairingList.forEach(p => {
    if (!p.sectors || !p.sectors.length) return;
    const codes = parseSectors(p.sectors);
    for (let i = 0; i < codes.length - 1; i++) {
      const depCode = codes[i], arrCode = codes[i + 1];
      const dep = AIRPORTS[depCode], arr = AIRPORTS[arrCode];
      if (!dep || !arr) continue;
      const key = `${depCode}-${arrCode}`;
      if (!arcMap.has(key)) {
        arcMap.set(key, {
          path: arcPath(dep.lat, dep.lng, arr.lat, arr.lng),
          dep: depCode, arr: arrCode,
          tail: p.tail_number || p.tail,
          fdp: p.total_fdp_hours || p.fdp || "—",
          blockH: (() => {
            const detail = (p.sector_detail || []).find(sd => sd.dep === depCode && sd.arr === arrCode);
            return detail?.block_hours ?? "";
          })(),
          crew: p.crew_required || 2,
          sectors: p.sectors,
          count: 1,
        });
      } else {
        arcMap.get(key).count += 1;
      }
    }
  });
  return Array.from(arcMap.values());
}

// ─── Map bounds fitter ────────────────────────────────────────────────────────
function BoundsFitter({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 1) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [map, bounds]);
  return null;
}


// ─── Schedule Builder Panel (two-phase: existing schedule + extra legs) ──────
function ScenarioBuilderPanel({
  aircraft, selectedTail, currentBH, targetBH,
  fleetSettings, existingSectors = [],
  // Controlled by parent (so parent can read for save modal)
  turnaroundMin, scheduleTimeline, returnAirport, returnTime,
  minExtraDep, groundTimeMinutes,
  firstDepTime, onFirstDepTimeChange,
  additionalDepTime, onAdditionalDepTimeChange,
  legs, onAddLeg, onRemoveLeg,
}) {
  const toMins  = t => { const [h, m] = (t || "00:00").split(":").map(Number); return h * 60 + m; };
  const additionalDepMins = toMins(additionalDepTime || minExtraDep);
  const minExtraDepMins   = toMins(minExtraDep);
  const isTooEarly        = additionalDepMins < minExtraDepMins;

  // ── Additional legs chain ────────────────────────────────────────────────
  const lastLeg     = legs[legs.length - 1];
  const nextDepCode = legs.length === 0 ? returnAirport : (lastLeg?.arr || returnAirport);
  const thisDepTime = legs.length === 0
    ? (additionalDepTime || minExtraDep)
    : addMinutes(lastLeg.arrTime, turnaroundMin);

  const [nextArr, setNextArr] = useState("");
  // Reset nextArr on aircraft/base change
  const base = aircraft.find(a => a.tail_number === selectedTail)?.base || "DEL";
  useEffect(() => { setNextArr(""); }, [selectedTail, base]);

  const estBlockH   = nextDepCode && nextArr ? estimateBlockHours(nextDepCode, nextArr) : 0;
  const thisArrTime = thisDepTime && estBlockH > 0 ? addMinutes(thisDepTime, Math.round(estBlockH * 60)) : "";

  // Impact stats
  const totalScenarioBH = legs.reduce((s, l) => s + l.blockH, 0);
  const newTotal        = Math.round((currentBH + totalScenarioBH) * 10) / 10;
  const newUtil         = targetBH > 0 ? Math.round((newTotal / targetBH) * 100) : 0;
  const idleAfter       = Math.max(0, Math.round((targetBH - newTotal) * 10) / 10);
  const utilColor       = newUtil >= 70 ? "text-green-400" : newUtil >= 40 ? "text-amber-400" : "text-red-400";

  const sortedAirports = Object.entries(AIRPORTS)
    .filter(([k]) => k !== nextDepCode)
    .sort((x, y) => estimateBlockHours(nextDepCode, x[0]) - estimateBlockHours(nextDepCode, y[0]));

  const extraRouteLabel = legs.length > 0
    ? [returnAirport, ...legs.map(l => l.arr)].join(" → ")
    : "";

  const handleAddLeg = () => {
    if (!nextArr || estBlockH <= 0 || isTooEarly) return;
    const depTime = legs.length === 0
      ? (additionalDepTime || minExtraDep)
      : addMinutes(lastLeg.arrTime, turnaroundMin);
    const arrTime = addMinutes(depTime, Math.round(estBlockH * 60));
    onAddLeg({ id: `leg-${Date.now()}`, dep: nextDepCode, arr: nextArr, blockH: estBlockH, depTime, arrTime });
    setNextArr("");
  };

  return (
    <div className="flex-1 flex flex-col overflow-y-auto" data-testid="scenario-builder-panel">

      {/* ── Impact header ─────────────────────────────────────────────────── */}
      <div className="px-3 py-3 border-b border-border/30 space-y-3">
        <div className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">
          Schedule Builder · {MONTH_NAME}{selectedTail ? ` · ${selectedTail}` : ""}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            ["Original",   `${currentBH}h`,                               "text-amber-400"],
            ["+ Scenario", `+${Math.round(totalScenarioBH * 10) / 10}h`,  "text-indigo-400"],
            ["New Total",  `${newTotal}h`,                                 utilColor],
          ].map(([l, v, c]) => (
            <div key={l} className="bg-white/5 rounded-sm p-1.5 text-center">
              <div className="text-[8px] text-muted-foreground uppercase">{l}</div>
              <div className={`text-xs font-mono font-bold ${c} mt-0.5`}>{v}</div>
            </div>
          ))}
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-[8px] text-muted-foreground">
            <span>Current</span><span>{Math.round(currentBH / (targetBH || 1) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-amber-500/70" style={{ width: `${Math.min(100, currentBH / (targetBH || 1) * 100)}%` }} />
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${Math.min(100, newTotal / (targetBH || 1) * 100)}%` }} />
          </div>
          <div className="flex justify-between text-[8px]">
            <span className="text-indigo-400">With scenario</span>
            <span className={utilColor}>{newUtil}% util</span>
          </div>
        </div>
        {idleAfter > 0 && (
          <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-500/10 border border-amber-500/20 rounded-sm text-[9px] text-amber-400">
            <AlertTriangle size={9} /> {idleAfter}h still idle — add more sectors
          </div>
        )}
      </div>

      {/* ── Fleet Settings strip ─────────────────────────────────────────── */}
      <div className="px-3 py-1.5 border-b border-border/20 flex items-center gap-2 text-[9px] text-muted-foreground bg-amber-500/5">
        <span className="text-amber-400 font-mono font-bold">B737 MAX 8</span>
        <span>·</span>
        <span>Min ground: <span className="text-foreground font-bold">{turnaroundMin} min</span></span>
        <span className="text-muted-foreground/40">(Fleet Settings)</span>
      </div>

      {/* ── Phase 1: Existing scheduled sectors ─────────────────────────── */}
      <div className="px-3 py-2.5 border-b border-border/30 space-y-2">
        <div className="text-[9px] font-bold uppercase tracking-widest text-amber-400">
          {existingSectors.length > 0 ? "Scheduled Today" : "No Schedule Found"}
        </div>

        {existingSectors.length > 0 ? (
          <>
            {/* First sector departure time — anchors the whole existing schedule */}
            <div>
              <label className="text-[9px] text-muted-foreground mb-0.5 block">
                First Departure — {existingSectors[0].dep} → {existingSectors[0].arr} (local)
              </label>
              <input type="time" value={firstDepTime} onChange={e => onFirstDepTimeChange(e.target.value)}
                className="w-full bg-card border border-amber-500/40 rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                data-testid="scheduled-first-dep-time" />
            </div>

            {/* Computed schedule sectors */}
            <div className="space-y-1">
              {scheduleTimeline.map((sector, i) => (
                <div key={i}>
                  {i > 0 && (
                    <div className="flex items-center gap-1 py-0.5 pl-2">
                      <div className="w-px h-2.5 bg-amber-500/30" />
                      <span className="text-[8px] text-muted-foreground/60">
                        {turnaroundMin}min ground · {AIRPORTS[sector.dep]?.city}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between px-2 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-sm">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[8px] font-mono text-amber-400/60 font-bold w-5">S{i + 1}</span>
                        <span className="text-[10px] font-mono font-bold text-amber-300">{sector.dep} → {sector.arr}</span>
                      </div>
                      <div className="text-[9px] text-muted-foreground font-mono">
                        {sector.depTime} → {sector.arrTime} · {sector.blockH}h block
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Aircraft returned banner */}
            {returnTime && (
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-teal-500/10 border border-teal-500/25 rounded-sm text-[9px] text-teal-400">
                <Plane size={9} />
                Aircraft back at <span className="font-bold font-mono mx-1">{returnAirport}</span> · <span className="font-bold font-mono ml-1">{returnTime}</span>
              </div>
            )}
          </>
        ) : (
          <div className="text-[9px] text-muted-foreground/50 italic">
            No sectors found for this aircraft today. Select a tail number with a scheduled pairing.
          </div>
        )}
      </div>

      {/* ── Phase 2: Ground time picker at base (before extra departure) ── */}
      {returnTime && (
        <div className="px-3 py-2.5 border-b border-border/30 space-y-2">
          <div className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">
            Extra Departure from {returnAirport}
          </div>
          <div>
            <label className="text-[9px] text-muted-foreground mb-0.5 block">
              Departure Time (min: <span className="text-amber-400 font-mono">{minExtraDep}</span> · after {turnaroundMin}min min ground)
            </label>
            <input type="time" value={additionalDepTime}
              onChange={e => onAdditionalDepTimeChange(e.target.value)}
              className={`w-full bg-card border rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none transition-all ${
                isTooEarly
                  ? "border-red-500/60 focus:ring-1 focus:ring-red-500/40"
                  : "border-indigo-500/40 focus:ring-1 focus:ring-indigo-500/50"
              }`}
              data-testid="additional-dep-time" />
            {isTooEarly ? (
              <div className="text-[8px] text-red-400 mt-0.5 flex items-center gap-1">
                <AlertTriangle size={8} /> Before minimum departure {minExtraDep}
              </div>
            ) : (
              <div className="text-[8px] text-muted-foreground/60 mt-0.5">
                Ground at {returnAirport}: <span className="text-teal-400 font-bold">{groundTimeMinutes}min</span>
                {groundTimeMinutes > turnaroundMin && (
                  <span className="text-muted-foreground/40 ml-1">(+{groundTimeMinutes - turnaroundMin}min above minimum)</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Built extra legs ─────────────────────────────────────────────── */}
      {legs.length > 0 && (
        <div className="px-3 py-2.5 border-b border-border/30 space-y-1">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[9px] font-bold uppercase tracking-widest text-indigo-400">Extra Sectors</div>
            {extraRouteLabel && (
              <div className="text-[8px] font-mono text-indigo-300/70 truncate max-w-[130px]" title={extraRouteLabel}>
                {extraRouteLabel}
              </div>
            )}
          </div>
          {legs.map((leg, i) => (
            <div key={leg.id}>
              {i > 0 && (
                <div className="flex items-center gap-1 py-0.5 pl-2">
                  <div className="w-px h-2.5 bg-indigo-500/30" />
                  <span className="text-[8px] text-muted-foreground/60">
                    {turnaroundMin}min ground · {AIRPORTS[leg.dep]?.city}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between px-2 py-1.5 bg-indigo-500/10 border border-indigo-500/20 rounded-sm"
                data-testid={`schedule-leg-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[8px] font-mono text-indigo-400/60 font-bold w-5">E{i + 1}</span>
                    <span className="text-[10px] font-mono font-bold text-indigo-300">{leg.dep} → {leg.arr}</span>
                  </div>
                  <div className="text-[9px] text-muted-foreground font-mono">
                    {leg.depTime} → {leg.arrTime} · {leg.blockH}h block
                  </div>
                </div>
                <button onClick={() => onRemoveLeg(leg.id)}
                  className="p-1 text-red-400/40 hover:text-red-400 transition-colors flex-shrink-0" title="Remove">
                  <X size={9} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Next extra leg builder ───────────────────────────────────────── */}
      <div className="px-3 py-3 border-b border-border/30 space-y-2">
        <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
          {legs.length === 0
            ? existingSectors.length > 0
              ? `Add Sector — from ${returnAirport} after return`
              : "First Leg"
            : `Leg ${legs.length + 1} — continues from ${nextDepCode}`}
        </div>

        {/* Standalone first-leg dep time (only when no existing schedule) */}
        {!existingSectors.length && legs.length === 0 && (
          <div>
            <label className="text-[9px] text-muted-foreground mb-0.5 block">Departure Time (local)</label>
            <input type="time" value={firstDepTime} onChange={e => setFirstDepTime(e.target.value)}
              className="w-full bg-card border border-border/60 rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none"
              data-testid="first-leg-dep-time" />
          </div>
        )}

        {/* From (auto) */}
        <div>
          <label className="text-[9px] text-muted-foreground mb-0.5 block">
            From (auto{returnTime && legs.length === 0 ? ` · after return to ${returnAirport}` : ""})
          </label>
          <div className="px-2 py-1.5 bg-card/50 border border-border/40 rounded-sm text-xs font-mono text-foreground/70 flex items-center justify-between">
            <span>{nextDepCode} — {AIRPORTS[nextDepCode]?.city}</span>
            <span className="text-indigo-400/80 text-[9px]">dep {thisDepTime}</span>
          </div>
        </div>

        {/* To airport */}
        <div>
          <label className="text-[9px] text-muted-foreground mb-0.5 block">To (nearest first)</label>
          <select value={nextArr} onChange={e => setNextArr(e.target.value)}
            className="w-full bg-card border border-border/60 rounded-sm px-2 py-1.5 text-xs font-mono text-foreground focus:outline-none"
            data-testid="scenario-arr-select">
            <option value="">Select destination...</option>
            {sortedAirports.map(([k, v]) => (
              <option key={k} value={k}>{k} — {v.city} · {estimateBlockHours(nextDepCode, k)}h</option>
            ))}
          </select>
        </div>

        {/* Preview */}
        {nextArr && (
          <div className="p-2 bg-indigo-500/10 border border-indigo-500/25 rounded-sm space-y-1">
            <div className="font-mono text-[10px] font-bold text-indigo-300">{nextDepCode} → {nextArr}</div>
            <div className="flex gap-3 text-[9px] text-muted-foreground flex-wrap">
              <span>Dep <span className="text-foreground font-bold">{thisDepTime}</span></span>
              <span>→ Arr <span className="text-foreground font-bold">{thisArrTime}</span></span>
              <span>· <span className="text-indigo-300 font-bold">{estBlockH}h</span> block</span>
            </div>
            {legs.length === 0 && returnTime && (
              <div className="text-[8px] text-teal-400/80">
                {groundTimeMinutes}min ground at {returnAirport}
              </div>
            )}
            {legs.length > 0 && (
              <div className="text-[8px] text-amber-400/80">
                {turnaroundMin}min turnaround at {AIRPORTS[nextDepCode]?.city}
              </div>
            )}
          </div>
        )}

        <button onClick={handleAddLeg} disabled={!nextArr || isTooEarly}
          className="w-full py-1.5 text-[10px] font-bold rounded-sm bg-indigo-500/15 border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/25 disabled:opacity-40 transition-all"
          data-testid="add-leg-btn">
          <Plus size={10} className="inline mr-1" />
          {legs.length === 0
            ? `+ Add Sector: ${nextDepCode} → ${nextArr || "?"}`
            : `+ Leg ${legs.length + 1}: ${nextDepCode} → ${nextArr || "?"}`}
        </button>
      </div>

      {/* Empty state hint */}
      {legs.length === 0 && !nextArr && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-[10px] text-muted-foreground/40 italic px-4 py-6 leading-relaxed">
            {existingSectors.length > 0
              ? <>Set the first departure time above to anchor<br />the existing schedule, then add extra sectors<br />after the aircraft returns to {returnAirport}.</>
              : <>Set departure time & pick a destination<br />to start building a schedule.</>}
          </div>
        </div>
      )}
    </div>
  );
}



// ─── Calendar Strip ───────────────────────────────────────────────────────────
function CalendarStrip({ dailyStats, selectedDay, onSelect, targetDayBH }) {
  return (
    <div
      className="flex items-end gap-px px-3 pt-2 pb-1 bg-background/98 border-t border-border/30 flex-shrink-0 overflow-x-auto print-hide"
      style={{ height: 60 }}
      data-testid="calendar-strip"
    >
      {Array.from({ length: DAYS_IN_MONTH }, (_, i) => {
        const d    = i + 1;
        const stat = dailyStats[i] || { totalBH: 0, pairingCount: 0 };
        const pct  = targetDayBH > 0 ? Math.min(100, (stat.totalBH / targetDayBH) * 100) : 0;
        const isToday    = d === TODAY_DAY;
        const isSelected = d === selectedDay;
        const isPast     = d < TODAY_DAY;
        const isFuture   = d > TODAY_DAY;

        let barColor;
        if (stat.pairingCount === 0) barColor = "rgba(255,255,255,0.06)";
        else if (pct >= 70)          barColor = "#22c55e";
        else if (pct >= 40)          barColor = "#f59e0b";
        else                         barColor  = "#ef4444";

        return (
          <button
            key={d}
            onClick={() => onSelect(d)}
            data-testid={`cal-day-${d}`}
            title={`Day ${d}: ${stat.totalBH}h / ${targetDayBH}h  (${stat.pairingCount} pairings)`}
            className={`flex-shrink-0 flex flex-col items-center gap-0.5 rounded-sm transition-all ${isSelected ? "ring-1 ring-white/70 bg-white/10" : "hover:bg-white/[0.05]"}`}
            style={{ width: 30, height: 46 }}
          >
            {/* Bar */}
            <div
              className="relative flex items-end overflow-hidden rounded-sm"
              style={{ width: 20, height: 28, backgroundColor: "rgba(255,255,255,0.05)" }}
            >
              <div
                className="w-full rounded-sm transition-all"
                style={{
                  height: `${Math.max(stat.pairingCount > 0 ? 5 : 0, pct)}%`,
                  backgroundColor: barColor,
                  opacity: isPast ? 0.5 : isFuture ? 0.85 : 1,
                }}
              />
              {isToday && (
                <div className="absolute inset-0 rounded-sm border border-amber-400/80 pointer-events-none" />
              )}
            </div>

            {/* Day label */}
            <span
              className={`text-[8px] font-mono leading-none ${
                isSelected  ? "text-white font-bold" :
                isToday     ? "text-amber-400 font-bold" :
                isPast      ? "text-white/25" :
                              "text-white/40"
              }`}
            >
              {d}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Day Pairing Panel ────────────────────────────────────────────────────────
function DayPairingPanel({ label, color, pairings, day }) {
  const [open, setOpen] = useState(true);
  if (!pairings || !pairings.length) return null;

  return (
    <div className="bg-card border rounded-sm overflow-hidden" style={{ borderColor: color + "55" }}>
      <button
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex items-center gap-2">
          <div className="w-3 h-2 rounded-sm" style={{ backgroundColor: color }} />
          <span className="text-xs font-bold text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">({pairings.length} on Day {day})</span>
        </div>
        <ChevronDown size={11} className={`text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-border/40 divide-y divide-border/20 max-h-64 overflow-y-auto">
          {pairings.map((p, i) => {
            const codes = parseSectors(p.sectors || []);
            const route = codes.join(" → ");
            return (
              <div key={p.id || i} className="px-3 py-2 space-y-1">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-mono font-bold text-foreground truncate">{route || "—"}</div>
                  <div className="text-[10px] text-muted-foreground font-mono flex-shrink-0 ml-2">{p.tail_number || p.tail || ""}</div>
                </div>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1 text-amber-300/80" title="Total block hours (airborne time)">
                    <Plane size={9} /> {getPairingBlockHours(p)}h block
                  </span>
                  <span className="flex items-center gap-1" title="Flight Duty Period (report to debrief)">
                    <Clock size={9} /> {p.total_fdp_hours || "—"}h FDP
                  </span>
                  <span className="flex items-center gap-1">
                    <Users size={9} /> {p.crew_required || 2} crew
                  </span>
                  <span className="flex items-center gap-1">
                    ✈ {(p.sectors || []).length} sec
                  </span>
                </div>
                {(p.sectors || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {p.sectors.map((s, j) => (
                      <span key={j} className="text-[9px] font-mono px-1.5 py-0.5 rounded-sm"
                        style={{ backgroundColor: color + "20", color, border: `1px solid ${color}40` }}>
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main Route Map Component ─────────────────────────────────────────────────
export default function PairingRouteMap({ pairings = [], optimResult = null, aircraft = [], activeBase = "", fleetSettings = null, mapScenario = null }) {
  const [utilData,      setUtilData]      = useState(null);
  const [selectedTail,  setSelectedTail]  = useState("");
  const [selectedDay,   setSelectedDay]   = useState(TODAY_DAY);
  const [saveModal,     setSaveModal]     = useState(false);
  const [saveName,      setSaveName]      = useState("");
  const [saveBase,      setSaveBase]      = useState(activeBase || "DEL");
  const [saving,        setSaving]        = useState(false);
  const [showCurrent,   setShowCurrent]   = useState(true);
  const [showOptimised, setShowOptimised] = useState(true);

  // ── Scenario Builder state ────────────────────────────────────────────────
  const [scenarioBuildMode,   setScenarioBuildMode]   = useState(false);
  const [scenarioLegs,        setScenarioLegs]        = useState([]);
  // Lifted from ScenarioBuilderPanel so parent can read them for save
  const [sbFirstDepTime,      setSbFirstDepTime]      = useState("06:00");
  const [sbAdditionalDepTime, setSbAdditionalDepTime] = useState("");

  // Guard ref: prevents the tail/day reset effect from wiping freshly-loaded scenario legs
  const skipNextReset = useRef(false);

  // Reset everything when aircraft or day changes
  useEffect(() => {
    if (skipNextReset.current) {
      skipNextReset.current = false;
      return;
    }
    setScenarioLegs([]);
    setSbFirstDepTime("06:00");
    setSbAdditionalDepTime("");
  }, [selectedTail, selectedDay]);

  // Hydrate state from a loaded saved scenario
  useEffect(() => {
    if (!mapScenario?.schedule) return;
    const s = mapScenario.schedule;
    skipNextReset.current = true;
    setSelectedTail(s.tail_number || "");
    setSelectedDay(s.date_day || TODAY_DAY);
    setSbFirstDepTime(s.first_dep_time || "06:00");
    setSbAdditionalDepTime(s.additional_dep_time || "");
    setScenarioLegs(s.extra_legs || []);
    setScenarioBuildMode(true);
  }, [mapScenario]); // eslint-disable-line

  useEffect(() => {
    crewOpsApi.getUtilization(activeBase || undefined)
      .then(r => setUtilData(r.data))
      .catch(() => {});
  }, [activeBase]);

  // ── Scope filter (tail / base) ────────────────────────────────────────────
  const scopePairings = useMemo(() => {
    const flightOnly = pairings.filter(p => !p.pairing_type || p.pairing_type === "flight");
    if (selectedTail) return flightOnly.filter(p => p.tail_number === selectedTail);
    if (activeBase)   return flightOnly.filter(p => {
      const ac = aircraft.find(a => a.tail_number === p.tail_number);
      return ac?.base === activeBase;
    });
    return flightOnly;
  }, [pairings, selectedTail, activeBase, aircraft]);

  const optimisedPairings = useMemo(() => {
    const all = (optimResult?.optimised_pairings || []).map((op, i) => ({
      ...op, tail_number: op.tail_number || op.tail || `opt-${i}`, id: op.id || `opt-${i}`,
    }));
    if (selectedTail) return all.filter(p => (p.tail_number || p.tail) === selectedTail);
    if (activeBase)   return all.filter(p => {
      const ac = aircraft.find(a => a.tail_number === (p.tail_number || p.tail));
      return ac?.base === activeBase;
    });
    return all;
  }, [optimResult, selectedTail, activeBase, aircraft]);

  // ── Per-day statistics (from scope, not day-filtered) ─────────────────────
  const dailyStats = useMemo(() => {
    return Array.from({ length: DAYS_IN_MONTH }, (_, i) => {
      const d = i + 1;
      const dayPairings = scopePairings.filter(p => {
        const sd = parseInt((p.start_date || "").split("-")[2] || "0");
        const ed = parseInt((p.end_date || "").split("-")[2] || "0");
        return sd <= d && d <= ed;
      });
      const totalBH   = dayPairings.reduce((s, p) => s + getPairingBlockHours(p), 0);
      const tailCount = new Set(dayPairings.map(p => p.tail_number)).size;
      return { day: d, totalBH: Math.round(totalBH * 10) / 10, pairingCount: dayPairings.length, tailCount };
    });
  }, [scopePairings]);

  // ── Day-filtered pairings for the map & panel ──────────────────────────────
  const dayPairings = useMemo(() => {
    return scopePairings.filter(p => {
      const sd = parseInt((p.start_date || "").split("-")[2] || "0");
      const ed = parseInt((p.end_date || "").split("-")[2] || "0");
      return sd <= selectedDay && selectedDay <= ed;
    });
  }, [scopePairings, selectedDay]);

  // Existing sectors for selected tail on selected day — anchors Schedule Builder Phase 1
  const existingSectors = useMemo(() => {
    if (!selectedTail) return [];
    return dayPairings
      .filter(p => p.tail_number === selectedTail || p.tail === selectedTail)
      .flatMap(p =>
        (p.sectors || []).map(s => {
          const parts = s.split("-");
          return parts.length === 2 ? { dep: parts[0], arr: parts[1] } : null;
        }).filter(Boolean)
      );
  }, [dayPairings, selectedTail]);

  // Turnaround min from fleet settings — shared between parent (save modal) and child panel
  const turnaroundMin = useMemo(() => {
    if (!fleetSettings?.fleet_types) return 45;
    const ft = fleetSettings.fleet_types.find(f => f.fleet_type === "B737 MAX 8");
    return ft?.min_ground_min ?? 45;
  }, [fleetSettings]);

  // Phase 1 schedule timeline — computed in parent so save modal can use it
  const scheduleTimeline = useMemo(() => {
    if (!existingSectors.length) return [];
    let cur = sbFirstDepTime;
    return existingSectors.map(s => {
      const blockH  = estimateBlockHours(s.dep, s.arr);
      const depTime = cur;
      const arrTime = addMinutes(depTime, Math.round(blockH * 60));
      cur = addMinutes(arrTime, turnaroundMin);
      return { ...s, blockH, depTime, arrTime };
    });
  }, [existingSectors, sbFirstDepTime, turnaroundMin]);

  const returnAirport     = scheduleTimeline.length > 0 ? scheduleTimeline[scheduleTimeline.length - 1].arr : (aircraft.find(a => a.tail_number === selectedTail)?.base || "DEL");
  const returnTime        = scheduleTimeline.length > 0 ? scheduleTimeline[scheduleTimeline.length - 1].arrTime : "";
  const minExtraDep       = returnTime ? addMinutes(returnTime, turnaroundMin) : sbFirstDepTime;
  const toMinsP           = t => { const [h, m] = (t || "00:00").split(":").map(Number); return h * 60 + m; };
  const groundTimeMinutes = returnTime ? Math.max(0, toMinsP(sbAdditionalDepTime || minExtraDep) - toMinsP(returnTime)) : turnaroundMin;

  // Sync sbAdditionalDepTime to minimum when schedule changes
  useEffect(() => {
    if (!minExtraDep) return;
    if (!sbAdditionalDepTime || toMinsP(sbAdditionalDepTime) < toMinsP(minExtraDep)) {
      setSbAdditionalDepTime(minExtraDep);
    }
  }, [minExtraDep]); // eslint-disable-line

  const dayOptimised = useMemo(() => {
    return optimisedPairings.filter(op => {
      const sd = op.start_day || parseInt((op.start_date || "").split("-")[2] || "0");
      const ed = op.end_day   || parseInt((op.end_date   || "").split("-")[2] || "0");
      return sd <= selectedDay && selectedDay <= ed;
    });
  }, [optimisedPairings, selectedDay]);

  // ── Daily utilisation summary for the selected day ────────────────────────
  const targetBHDay    = utilData?.target_bh_day || 12;
  const activeAcCount  = selectedTail ? 1
    : aircraft.filter(a => a.status === "active" && (!activeBase || a.base === activeBase)).length || 1;
  const targetDayBH    = Math.round(targetBHDay * activeAcCount * 10) / 10;
  const selectedStat   = dailyStats[selectedDay - 1] || { totalBH: 0, pairingCount: 0, tailCount: 0 };
  const dayUtilPct     = targetDayBH > 0 ? Math.round((selectedStat.totalBH / targetDayBH) * 100) : 0;
  const dayIdleBH      = Math.max(0, Math.round((targetDayBH - selectedStat.totalBH) * 10) / 10);

  // ── Arc data ──────────────────────────────────────────────────────────────
  const currentArcs   = showCurrent   ? buildArcs(dayPairings)  : [];
  const optimisedArcs = showOptimised ? buildArcs(dayOptimised) : [];

  // ── Scenario arcs — built directly from legs (indigo) ────────────────────
  const scenarioArcs = useMemo(() => {
    if (!scenarioLegs.length) return [];
    const arcMap = new Map();
    scenarioLegs.forEach(leg => {
      const dep = AIRPORTS[leg.dep], arr = AIRPORTS[leg.arr];
      if (!dep || !arr) return;
      const key = `${leg.dep}-${leg.arr}`;
      if (!arcMap.has(key)) {
        arcMap.set(key, {
          path: arcPath(dep.lat, dep.lng, arr.lat, arr.lng),
          dep: leg.dep, arr: leg.arr,
          blockH: leg.blockH,
          depTime: leg.depTime,
          arrTime: leg.arrTime,
        });
      }
    });
    return Array.from(arcMap.values());
  }, [scenarioLegs]);

  // Combined block hours: original + scenario additions
  const totalScenarioBH = scenarioLegs.reduce((s, l) => s + l.blockH, 0);
  const combinedBH      = Math.round((selectedStat.totalBH + totalScenarioBH) * 10) / 10;
  const combinedUtil    = targetDayBH > 0 ? Math.round((combinedBH / targetDayBH) * 100) : 0;
  const combinedIdle    = Math.max(0, Math.round((targetDayBH - combinedBH) * 10) / 10);

  // ── Visible airports ──────────────────────────────────────────────────────
  const visibleCodes = new Set();
  [...currentArcs, ...optimisedArcs, ...scenarioArcs].forEach(a => { visibleCodes.add(a.dep); visibleCodes.add(a.arr); });
  const visibleAirports = Object.entries(AIRPORTS).filter(([k]) => visibleCodes.has(k));

  const allLats = visibleAirports.map(([, v]) => v.lat);
  const allLngs = visibleAirports.map(([, v]) => v.lng);
  const bounds  = allLats.length > 1
    ? [[Math.min(...allLats) - 1, Math.min(...allLngs) - 1], [Math.max(...allLats) + 1, Math.max(...allLngs) + 1]]
    : null;

  const tails = [...new Set(aircraft.filter(a => a.status === "active").map(a => a.tail_number))].sort();

  const utilColor = dayUtilPct >= 70 ? "text-green-400" : dayUtilPct >= 40 ? "text-amber-400" : "text-red-400";
  const utilBg    = dayUtilPct >= 70 ? "bg-green-500/10 border-green-500/25" : dayUtilPct >= 40 ? "bg-amber-500/10 border-amber-500/25" : "bg-red-500/10 border-red-500/25";

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      const utilSummary = utilData
        ? { fleet_avg_util_pct: utilData.fleet_avg_util_pct, total_lost_bh: utilData.total_lost_bh, target_bh_day: utilData.target_bh_day }
        : null;
      await crewOpsApi.savePairingScenario({
        name: saveName.trim(),
        base: saveBase,
        description: `${MONTH_NAME} ${selectedDay} — ${selectedTail || saveBase || "All Fleet"} · ${scheduleTimeline.length + scenarioLegs.length} sectors`,
        pairings: dayPairings,
        optimisation_result: optimResult,
        utilization_summary: utilSummary,
        schedule: {
          tail_number:        selectedTail,
          date_day:           selectedDay,
          month:              MONTH_NAME,
          first_dep_time:     sbFirstDepTime,
          additional_dep_time: sbAdditionalDepTime,
          scheduled_sectors:  scheduleTimeline,
          extra_legs:         scenarioLegs,
          total_scheduled_bh: scheduleTimeline.reduce((s, x) => s + x.blockH, 0),
          total_extra_bh:     scenarioLegs.reduce((s, l) => s + l.blockH, 0),
          ground_time_min:    groundTimeMinutes,
          turnaround_min:     turnaroundMin,
          return_airport:     returnAirport,
          return_time:        returnTime,
        },
      });
      toast.success("Scenario saved");
      setSaveModal(false);
      setSaveName("");
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="route-map-tab">

      {/* ── Toolbar ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/30 bg-background/50 flex-shrink-0 flex-wrap">

        {/* Aircraft filter */}
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-muted-foreground uppercase tracking-widest">Aircraft</span>
          <select value={selectedTail} onChange={e => setSelectedTail(e.target.value)}
            className="bg-card border border-border rounded-sm px-2 py-1 text-xs text-foreground font-mono focus:outline-none"
            data-testid="aircraft-filter-select">
            <option value="">All Fleet</option>
            {aircraft.filter(a => a.status === "active").sort((a, b) => a.tail_number.localeCompare(b.tail_number)).map(a => (
              <option key={a.tail_number} value={a.tail_number}>{a.tail_number} · {acTypeShort(a.aircraft_type)}</option>
            ))}
          </select>
        </div>

        {/* Day navigation */}
        <div className="flex items-center gap-0.5 bg-card border border-border/60 rounded-sm">
          <button
            onClick={() => setSelectedDay(d => Math.max(1, d - 1))}
            disabled={selectedDay <= 1}
            className="px-2 py-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
            data-testid="prev-day-btn"
          >
            <ChevronLeft size={11} />
          </button>
          <span className="text-[10px] font-bold font-mono text-foreground px-2 min-w-20 text-center" data-testid="selected-day-label">
            {MONTH_NAME} {selectedDay}
            {selectedDay === TODAY_DAY && <span className="text-amber-400 ml-1">·today</span>}
          </span>
          <button
            onClick={() => setSelectedDay(d => Math.min(DAYS_IN_MONTH, d + 1))}
            disabled={selectedDay >= DAYS_IN_MONTH}
            className="px-2 py-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
            data-testid="next-day-btn"
          >
            <ChevronRight size={11} />
          </button>
        </div>

        {/* Daily BH utilisation badge — shows combined when scenario is active */}
        <div className={`flex items-center gap-2 px-2.5 py-1 border rounded-sm text-[10px] ${
          scenarioBuildMode && totalScenarioBH > 0
            ? "bg-indigo-500/10 border-indigo-500/25"
            : utilBg
        }`}>
          <TrendingUp size={10} className={scenarioBuildMode && totalScenarioBH > 0 ? "text-indigo-400" : utilColor} />
          {scenarioBuildMode && totalScenarioBH > 0 ? (
            <>
              <span className="text-amber-400 font-mono">{selectedStat.totalBH}h</span>
              <span className="text-muted-foreground">+</span>
              <span className="text-indigo-300 font-mono font-bold">{Math.round(totalScenarioBH * 10) / 10}h scen.</span>
              <span className="text-muted-foreground">=</span>
              <span className={`font-mono font-bold ${combinedUtil >= 70 ? "text-green-400" : combinedUtil >= 40 ? "text-amber-400" : "text-red-400"}`}>{combinedBH}h / {targetDayBH}h = {combinedUtil}%</span>
              {combinedIdle > 0 && <span className="text-amber-400/70">{combinedIdle}h idle</span>}
            </>
          ) : (
            <>
              <span className={`font-mono font-bold ${utilColor}`}>{selectedStat.totalBH}h</span>
              <span className="text-muted-foreground">/ {targetDayBH}h target</span>
              <span className={`font-bold ${utilColor}`}>{dayUtilPct}%</span>
              {dayIdleBH > 0 && (
                <span className="text-red-400/70 flex items-center gap-0.5">
                  <AlertTriangle size={8} /> {dayIdleBH}h idle
                </span>
              )}
            </>
          )}
        </div>

        {/* Layer toggles */}
        <div className="flex items-center gap-1.5">
          <button onClick={() => setShowCurrent(c => !c)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[10px] font-bold transition-all ${showCurrent ? "bg-amber-500/20 border-amber-500/50 text-amber-300" : "bg-card border-border text-muted-foreground"}`}
            data-testid="toggle-current-routes">
            <div className="w-4 h-0.5 bg-amber-400 rounded" /> Current
          </button>
          <button onClick={() => setShowOptimised(c => !c)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[10px] font-bold transition-all ${showOptimised ? "bg-teal-500/20 border-teal-500/50 text-teal-300" : "bg-card border-border text-muted-foreground"}`}
            data-testid="toggle-optimised-routes">
            <div className="w-4 h-0.5 border-t-2 border-dashed border-teal-400 rounded" /> Optimised
          </button>
          <button
            onClick={() => setScenarioBuildMode(b => !b)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[10px] font-bold transition-all ${scenarioBuildMode ? "bg-indigo-500/20 border-indigo-500/50 text-indigo-300" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}
            data-testid="toggle-scenario-builder">
            <div className="w-4 h-0.5 border-t-2 border-dotted border-indigo-400 rounded" /> Scenario
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setSaveModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25 transition-all"
            data-testid="save-scenario-btn">
            <Save size={10} /> Save Scenario
          </button>
          <button onClick={() => window.print()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded-sm bg-card border border-border text-muted-foreground hover:text-foreground transition-all"
            data-testid="print-map-btn">
            <Printer size={10} /> Print
          </button>
        </div>
      </div>

      {/* ── Map + Side Panel ──────────────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* Leaflet map */}
        <div className="flex-1 relative flex flex-col print-map">
          <div className="flex-1 relative">
            <MapContainer center={[22.5, 78.5]} zoom={5} className="h-full w-full" zoomControl={true}>
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                maxZoom={18}
              />
              {bounds && <BoundsFitter bounds={bounds} />}

              {/* Current plan arcs — amber solid */}
              {currentArcs.map((arc, i) => (
                <Polyline key={`cur-${i}`} positions={arc.path}
                  pathOptions={{ color: "#f59e0b", weight: Math.min(2 + arc.count * 0.4, 5), opacity: 0.85 }}>
                  <Popup>
                    <div className="text-xs space-y-1 min-w-40">
                      <div className="font-bold text-amber-400">{arc.dep} → {arc.arr}</div>
                      <div className="text-gray-400">Current Plan · {MONTH_NAME} {selectedDay}</div>
                      <div className="font-mono">{arc.blockH}h block · {arc.fdp}h FDP · {arc.crew} crew</div>
                      {arc.count > 1 && <div className="text-[10px] text-amber-300">+{arc.count} rotations today</div>}
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Optimised plan arcs — teal dashed */}
              {optimisedArcs.map((arc, i) => (
                <Polyline key={`opt-${i}`} positions={arc.path}
                  pathOptions={{ color: "#2dd4bf", weight: Math.min(2 + (arc.count || 1) * 0.4, 5), opacity: 0.9, dashArray: "7 4" }}>
                  <Popup>
                    <div className="text-xs space-y-1 min-w-40">
                      <div className="font-bold text-teal-400">{arc.dep} → {arc.arr}</div>
                      <div className="text-gray-400">AI Optimised · {MONTH_NAME} {selectedDay}</div>
                      <div className="font-mono">{arc.blockH ? `${arc.blockH}h block · ` : ""}{arc.fdp}h FDP · {arc.crew} crew</div>
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Scenario arcs — indigo dotted (user-built what-if additions) */}
              {scenarioBuildMode && scenarioArcs.map((arc, i) => (
                <Polyline key={`scen-${i}`} positions={arc.path}
                  pathOptions={{ color: "#818cf8", weight: 2.5, opacity: 0.95, dashArray: "3 3" }}>
                  <Popup>
                    <div className="text-xs space-y-1 min-w-36">
                      <div className="font-bold text-indigo-400">{arc.dep} → {arc.arr}</div>
                      <div className="text-gray-400">Scenario Leg</div>
                      <div className="font-mono">{arc.blockH}h block (est.)</div>
                      {arc.depTime && <div className="text-indigo-300">{arc.depTime} → {arc.arrTime}</div>}
                    </div>
                  </Popup>
                </Polyline>
              ))}

              {/* Airport markers */}
              {visibleAirports.map(([iata, ap]) => (
                <CircleMarker key={iata} center={[ap.lat, ap.lng]}
                  radius={5} pathOptions={{ color: "#fff", fillColor: "#1e293b", weight: 1.5, fillOpacity: 1 }}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold">{iata}</div>
                      <div className="text-gray-400">{ap.city}</div>
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>

            {/* Empty day state overlay */}
            {currentArcs.length === 0 && optimisedArcs.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[1000]">
                <div className="bg-background/85 backdrop-blur-sm border border-border rounded-sm px-5 py-3 text-center">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                    {MONTH_NAME} {selectedDay}
                  </div>
                  <div className="text-xs text-muted-foreground">No flights scheduled for {selectedTail || "this fleet"}</div>
                  <div className="text-[10px] text-red-400 mt-1">Idle day — 0h / {targetDayBH}h target</div>
                </div>
              </div>
            )}

            {/* Map legend overlay */}
            <div className="absolute bottom-3 left-3 z-[1000] bg-background/90 backdrop-blur-sm border border-border rounded-sm px-3 py-2 space-y-1.5 text-[10px]">
              {showCurrent && (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 bg-amber-400 rounded" />
                  <span className="text-muted-foreground">Current Plan</span>
                </div>
              )}
              {showOptimised && (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 border-t-2 border-dashed border-teal-400" />
                  <span className="text-muted-foreground">Optimised Plan</span>
                </div>
              )}
              {scenarioBuildMode && scenarioArcs.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-0.5 border-t-2 border-dotted border-indigo-400" />
                  <span className="text-indigo-400 font-bold">Scenario (what-if)</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full border border-white bg-slate-900" />
                <span className="text-muted-foreground">Airport</span>
              </div>
            </div>
          </div>

          {/* Calendar strip */}
          <CalendarStrip
            dailyStats={dailyStats}
            selectedDay={selectedDay}
            onSelect={setSelectedDay}
            targetDayBH={targetDayBH}
          />
        </div>

        {/* Side panel — switches between Day Pairings and Scenario Builder */}
        <div className="w-72 border-l border-border/40 flex flex-col bg-card/30 flex-shrink-0 print-hide overflow-hidden">
          {/* Panel header */}
          <div className="px-3 py-3 border-b border-border/30 flex-shrink-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
              {scenarioBuildMode ? "Schedule Builder" : MONTH_NAME + " " + selectedDay}
              {" · "}{selectedTail || (activeBase ? `${activeBase} Base` : "All Fleet")}
            </div>
            {/* Stats grid — shows combined when scenario is active */}
            <div className="grid grid-cols-3 gap-1.5">
              {(scenarioBuildMode && totalScenarioBH > 0 ? [
                ["Current",  `${selectedStat.totalBH}h`, "text-amber-400"],
                ["Scenario", `+${Math.round(totalScenarioBH * 10) / 10}h`, "text-indigo-400"],
                ["New Util", `${combinedUtil}%`, combinedUtil >= 70 ? "text-green-400" : combinedUtil >= 40 ? "text-amber-400" : "text-red-400"],
              ] : [
                ["Block Hrs", `${selectedStat.totalBH}h`, utilColor],
                ["Target",   `${targetDayBH}h`,           "text-muted-foreground"],
                ["Util",     `${dayUtilPct}%`,             utilColor],
              ]).map(([l, v, c]) => (
                <div key={l} className="bg-white/5 rounded-sm p-1.5 text-center">
                  <div className="text-[8px] text-muted-foreground uppercase">{l}</div>
                  <div className={`text-xs font-mono font-bold ${c} mt-0.5`}>{v}</div>
                </div>
              ))}
            </div>
            {!scenarioBuildMode && dayIdleBH > 0 && (
              <div className="mt-2 px-2 py-1.5 bg-red-500/10 border border-red-500/20 rounded-sm text-[10px] text-red-400 flex items-center gap-1.5">
                <AlertTriangle size={9} />
                {dayIdleBH}h idle — <button className="underline hover:text-red-300" onClick={() => setScenarioBuildMode(true)}>Build a schedule</button>
              </div>
            )}
          </div>

          {/* Panel body — conditional */}
          {scenarioBuildMode ? (
            <ScenarioBuilderPanel
              aircraft={aircraft}
              selectedTail={selectedTail}
              currentBH={selectedStat.totalBH}
              targetBH={targetDayBH}
              fleetSettings={fleetSettings}
              existingSectors={existingSectors}
              turnaroundMin={turnaroundMin}
              scheduleTimeline={scheduleTimeline}
              returnAirport={returnAirport}
              returnTime={returnTime}
              minExtraDep={minExtraDep}
              groundTimeMinutes={groundTimeMinutes}
              firstDepTime={sbFirstDepTime}
              onFirstDepTimeChange={setSbFirstDepTime}
              additionalDepTime={sbAdditionalDepTime}
              onAdditionalDepTimeChange={setSbAdditionalDepTime}
              legs={scenarioLegs}
              onAddLeg={leg => setScenarioLegs(prev => [...prev, leg])}
              onRemoveLeg={id => setScenarioLegs(prev => prev.filter(l => l.id !== id))}
            />
          ) : (
            <div className="flex-1 p-3 space-y-3 overflow-y-auto">
              {dayPairings.length === 0 && dayOptimised.length === 0 ? (
                <div className="text-[10px] text-muted-foreground/50 italic text-center py-6">
                  No flights on {MONTH_NAME} {selectedDay}.<br />
                  Use ← → or the calendar to navigate.
                </div>
              ) : (
                <>
                  <DayPairingPanel label="Current Plan" color="#f59e0b" pairings={dayPairings} day={selectedDay} />
                  {dayOptimised.length > 0 && (
                    <DayPairingPanel label="Optimised Plan" color="#2dd4bf" pairings={dayOptimised} day={selectedDay} />
                  )}
                  {dayOptimised.length === 0 && (
                    <div className="text-[10px] text-muted-foreground/40 italic text-center py-3">
                      Run "Optimise Fleet (AI)" from the Gantt tab to see optimised overlay for this day.
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Schedule Preview & Save Modal ───────────────────────────────────── */}
      {saveModal && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-sm w-full max-w-lg flex flex-col" style={{ maxHeight: "90vh" }}>

            {/* Header */}
            <div className="px-5 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
              <div>
                <div className="font-bold text-sm text-foreground">Save Schedule Scenario</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {MONTH_NAME} {selectedDay}
                  {selectedTail && <> · <span className="font-mono text-amber-400">{selectedTail}</span></>}
                  {" · "}{scheduleTimeline.length + scenarioLegs.length} sectors total
                </div>
              </div>
              <button onClick={() => setSaveModal(false)} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
                <X size={14} />
              </button>
            </div>

            {/* Schedule preview */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">

              {/* Phase 1 — Existing scheduled sectors */}
              {scheduleTimeline.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-amber-400 mb-2">Scheduled Sectors</div>
                  {scheduleTimeline.map((s, i) => (
                    <div key={i}>
                      {i > 0 && (
                        <div className="flex items-center gap-1.5 py-0.5 pl-3">
                          <div className="w-px h-3 bg-amber-500/30" />
                          <span className="text-[8px] text-muted-foreground/50">{turnaroundMin}min ground · {AIRPORTS[s.dep]?.city}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-sm">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[8px] font-mono text-amber-400/60 font-bold w-5">S{i + 1}</span>
                            <span className="text-[11px] font-mono font-bold text-amber-300">{s.dep} → {s.arr}</span>
                          </div>
                          <div className="text-[9px] text-muted-foreground font-mono">{s.depTime} → {s.arrTime} · {s.blockH}h block</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Aircraft return divider */}
              {scheduleTimeline.length > 0 && (returnTime || scenarioLegs.length > 0) && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border/30" />
                  <div className="flex items-center gap-1.5 text-[9px] text-teal-400">
                    <Plane size={9} />
                    <span>Aircraft at <span className="font-bold font-mono">{returnAirport}</span></span>
                    {returnTime && <span>· returned <span className="font-mono font-bold">{returnTime}</span></span>}
                    {groundTimeMinutes > 0 && <span>· <span className="font-bold">{groundTimeMinutes}min</span> ground</span>}
                  </div>
                  <div className="flex-1 h-px bg-border/30" />
                </div>
              )}

              {/* Phase 2 — Extra scenario legs */}
              {scenarioLegs.length > 0 && (
                <div>
                  <div className="text-[9px] font-bold uppercase tracking-widest text-indigo-400 mb-2">Extra Sectors</div>
                  {scenarioLegs.map((leg, i) => (
                    <div key={leg.id}>
                      {i > 0 && (
                        <div className="flex items-center gap-1.5 py-0.5 pl-3">
                          <div className="w-px h-3 bg-indigo-500/30" />
                          <span className="text-[8px] text-muted-foreground/50">{turnaroundMin}min ground · {AIRPORTS[leg.dep]?.city}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between px-3 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-sm">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[8px] font-mono text-indigo-400/60 font-bold w-5">E{i + 1}</span>
                            <span className="text-[11px] font-mono font-bold text-indigo-300">{leg.dep} → {leg.arr}</span>
                          </div>
                          <div className="text-[9px] text-muted-foreground font-mono">{leg.depTime} → {leg.arrTime} · {leg.blockH}h block</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {scheduleTimeline.length === 0 && scenarioLegs.length === 0 && (
                <div className="py-6 text-center text-[10px] text-muted-foreground/50 italic">
                  No schedule built yet. Add sectors in the Schedule Builder to save.
                </div>
              )}

              {/* BH summary */}
              {(scheduleTimeline.length > 0 || scenarioLegs.length > 0) && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["Original",  `${selectedStat.totalBH}h`,                                                    "text-amber-400"],
                    ["+ Extra",   `+${Math.round(scenarioLegs.reduce((s,l)=>s+l.blockH,0)*10)/10}h`,             "text-indigo-400"],
                    ["New Total", `${Math.round((selectedStat.totalBH + scenarioLegs.reduce((s,l)=>s+l.blockH,0))*10)/10}h`, "text-green-400"],
                  ].map(([l, v, c]) => (
                    <div key={l} className="bg-white/5 rounded-sm p-2 text-center border border-border/20">
                      <div className="text-[8px] text-muted-foreground uppercase">{l}</div>
                      <div className={`text-sm font-mono font-bold ${c} mt-0.5`}>{v}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Name + Base */}
              <div className="space-y-2 pt-1">
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">Scenario Name</label>
                  <input type="text" value={saveName} onChange={e => setSaveName(e.target.value)}
                    placeholder={`e.g. ${MONTH_NAME} ${selectedDay} — ${selectedTail || saveBase} Optimised`}
                    className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    data-testid="save-scenario-name-input" />
                </div>
                <div>
                  <label className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground block mb-1">Base</label>
                  <select value={saveBase} onChange={e => setSaveBase(e.target.value)}
                    className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none">
                    {["DEL","BOM","BLR","HYD","MAA","CCU","AMD","GOA"].map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-5 py-3 border-t border-border flex items-center gap-3 justify-end flex-shrink-0">
              <button onClick={() => setSaveModal(false)}
                className="px-3 py-1.5 text-xs text-muted-foreground border border-border rounded-sm hover:bg-white/5 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving || !saveName.trim()}
                className="flex items-center gap-1.5 px-5 py-1.5 text-xs font-bold rounded-sm bg-primary/20 border border-primary/50 text-primary hover:bg-primary/30 disabled:opacity-40 transition-all"
                data-testid="save-scenario-confirm-btn">
                {saving ? <RefreshCw size={10} className="animate-spin" /> : <Save size={10} />}
                {saving ? "Saving..." : "Save Scenario"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
